// 배포 단위로 하나의 브랜드 + 하나의 스테이지(real/beta)만 활성화된다(recorder_manager_view와 같은 방식).
//   - 브랜드: hiball | brochure | hanwha | giants
//   - 스테이지: real | beta (미지정 시 real)
// 빌드할 때 정해진다 — `npm run build:giants:beta`가 `vite build --mode giants-beta`를 실행하고
// vite.config.ts가 mode(또는 .env의 VITE_BRAND/VITE_STAGE)를 풀어 __APP_BRAND__/__APP_STAGE__로 박아 넣는다.
//
// brands.json 항목:
//   name: 브라우저 탭 제목 등에 쓰는 표시 이름
//   apiPort.{real|beta}: 이 브랜드·스테이지 API 서버 port(real = API prod-{brand} 프로파일, beta = dev-{brand} 프로파일).
//     로컬 개발(npm run dev:*) 시 vite 프록시가 /api 요청을 localhost:{port}로 넘긴다.
//     배포 환경에서는 웹서버가 /api를 프록시하므로 빌드 결과물에는 쓰이지 않는다.
//   theme.primary: 메인 색 — antd colorPrimary(선택된 버튼·링크·아바타)와 화면 상단 헤더 바(경기목록 사이드바
//     로고 글자, 기록입력·기록조회 상단 띠)에 쓴다.
//     hiball #0749a3(하이볼 로고 블루 — recorder_manager_view의 brochure와 같은 값) / brochure = hiball과 같은 값
//     (사용자 확정, 2026-10-16 — 처음엔 예전 이 앱의 주황 #f58220이었는데 한화 주황과 구분되지 않아 블루로 바꿨다) /
//     hanwha #ff6600(이글스 오렌지) / giants #14213d(네이비 — recorder_manager_view의 giants와 같은 값)
//   theme.background: 페이지 바탕색 — antd colorBgLayout(경기목록·기록조회 본문)과 로그인·기록입력 화면 바탕.
//     패널(흰 카드)과 구분될 만큼만 옅게 브랜드 색을 섞었다.
//   tokenCookieName: 로그인 후 이 앱의 JWT를 저장하는 쿠키 이름({brand}_recorder_access_token). 관리 화면
//     (recorder_manager_view, {brand}_access_token)과 이름이 같으면 서로의 토큰을 덮어써 상대 앱에서 로그아웃되므로
//     앱마다 다르게 둔다. API 프로파일의 Globals.auth.cookieLogin.tokenCookieName 과 같아야 쿠키 로그인이 된다.
//   tokenCookieDomain.{real|beta}: 그 쿠키의 허용 도메인(관리 화면과 같은 값 — 관리 화면 토큰 쿠키를 이 앱 서버도
//     받아야 관리 화면 로그인을 이어받는다). 현재 주소가 이 도메인에 속하지 않으면(로컬 localhost 등) 현재 호스트
//     전용으로 저장한다(shared/tokenStore.ts).
//   ⚠️ 이 화면에는 브랜드와 무관한 '의미 색'이 따로 있다 — 입력모드 초록/수정모드 보라(recordStyles.ts),
//     주자·스트라이크 주황(RunnerState/ScoreBoard), 구종 모양 색(PitchShapeIcon, 서버 siteOwner 기준).
//     브랜드 색을 바꿀 때 이것들을 함께 바꾸지 말 것(특히 수정모드 보라는 메인 색과 구분돼야 한다).
//
// ⚠️ 로그인 방식(form/cookie)·로그인 주소·siteOwner(구종 모양) 같은 테넌트 값은 여기 두지 않는다 —
// 지금처럼 서버가 /api/config로 내려준다(백엔드 프로파일이 기준이라 프론트와 어긋날 일이 없다).
import BRAND_DATA from './brands.json'

export type BrandId = keyof typeof BRAND_DATA
export type Stage = 'real' | 'beta'

export interface BrandTheme {
  /** 메인 색(antd colorPrimary, 상단 헤더 바) */
  primary: string
  /** 페이지 바탕색(antd colorBgLayout) */
  background: string
}

export interface Brand {
  id: string
  name: string
  apiPort: Partial<Record<Stage, number>>
  theme: BrandTheme
  /** 로그인 토큰 쿠키 이름 */
  tokenCookieName: string
  /** 로그인 토큰 쿠키 허용 도메인(스테이지별) */
  tokenCookieDomain: Partial<Record<Stage, string>>
}

export const BRANDS: Record<BrandId, Brand> = BRAND_DATA
export const DEFAULT_BRAND_ID: BrandId = 'hiball'
export const STAGES: Stage[] = ['real', 'beta']
export const DEFAULT_STAGE: Stage = 'real'

const isBrandId = (v: unknown): v is BrandId => typeof v === 'string' && v in BRANDS

export function getActiveBrandId(): BrandId {
  const id = typeof __APP_BRAND__ === 'string' ? __APP_BRAND__ : ''
  return isBrandId(id) ? id : DEFAULT_BRAND_ID
}

export function getActiveBrand(): Brand {
  return BRANDS[getActiveBrandId()]
}

/** 빌드한 브랜드의 메인 색·바탕색 */
export function getBrandTheme(): BrandTheme {
  return getActiveBrand().theme
}

export function getActiveStage(): Stage {
  const stage = typeof __APP_STAGE__ === 'string' ? __APP_STAGE__ : ''
  return (STAGES as string[]).includes(stage) ? (stage as Stage) : DEFAULT_STAGE
}

export function isBetaStage(): boolean {
  return getActiveStage() === 'beta'
}

/** 로그인 토큰(JWT) 쿠키 이름 (brands.json tokenCookieName). 없으면 recorder_access_token */
export function getTokenCookieName(): string {
  return (getActiveBrand().tokenCookieName || 'recorder_access_token').trim()
}

/** 로그인 토큰 쿠키의 허용 도메인 (brands.json tokenCookieDomain.{real|beta}). 없으면 ''(현재 호스트 전용) */
export function getTokenCookieDomain(): string {
  return (getActiveBrand().tokenCookieDomain?.[getActiveStage()] || '').trim()
}

/** 브라우저 탭 제목 — beta 빌드는 real과 혼동하지 않도록 (BETA)를 붙인다 */
export function getAppTitle(): string {
  return `${getActiveBrand().name} 기록입력 시스템${isBetaStage() ? ' (BETA)' : ''}`
}
